import {
  Avatar,
  Badge,
  Box,
  Card,
  Flex,
  Spinner,
  Text,
} from '@chakra-ui/react'
import { useQuery } from '@tanstack/react-query'
import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { CircleFlag } from 'react-circle-flags'
import { fetchVacancyOverview } from '../../api/recruitment/recruitment'
import {
  VacancyOverview as VacancyOverviewType,
  VacancyOverviewCandidate,
  CANDIDATE_STATUS_COLORS,
  CANDIDATE_STATUS_LABELS,
  CandidateStatus,
} from '../../types/recruitment/recruitmentTypes'

const SOURCE_COLORS: Record<string, { bg: string; color: string }> = {
  referral: { bg: 'primary.0', color: 'primary.100' },
  website: { bg: 'aqua.0', color: 'aqua.100' },
  linkedin: { bg: 'aqua.0', color: 'aqua.100' },
  agency: { bg: 'warning.0', color: 'warning.100' },
  internal: { bg: 'success.0', color: 'success.100' },
}

const InterviewDots: React.FC<{ count: number; max?: number }> = ({ count, max = 5 }) => {
  const filled = Math.max(0, Math.min(max, count))
  return (
    <Flex align="center" gap="4px">
      {Array.from({ length: max }).map((_, idx) => (
        <Box
          key={idx}
          w="10px"
          h="10px"
          borderRadius="full"
          bg={idx < filled ? 'success.100' : 'solid.100'}
          transition="background 0.15s"
        />
      ))}
    </Flex>
  )
}

const VacancyOverview: React.FC = () => {
  const navigate = useNavigate()
  const [expandedVacancies, setExpandedVacancies] = useState<Set<string | null>>(() => new Set())

  const { data, isLoading } = useQuery({
    queryKey: ['vacancy-overview'],
    queryFn: fetchVacancyOverview,
  })

  const vacancies: VacancyOverviewType[] = data || []

  const toggleExpand = (id: string | null) => {
    setExpandedVacancies((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  if (isLoading) {
    return (
      <Card width="100%" p="0">
        <Flex justify="center" align="center" minH="400px">
          <Spinner size="lg" color="primary.100" />
        </Flex>
      </Card>
    )
  }

  if (vacancies.length === 0) {
    return (
      <Card width="100%" p="0">
        <Flex justify="center" align="center" minH="400px" direction="column" gap="8px">
          <Text textStyle="l-s" color="solid.400">
            No vacancies found
          </Text>
          <Text textStyle="b-xs" color="solid.300">
            Create a vacancy first to see candidates grouped by vacancy.
          </Text>
        </Flex>
      </Card>
    )
  }

  return (
    <Card width="100%" p="0" overflow="hidden">
      {/* Header */}
      <Flex
        px="20px"
        height="64px"
        align="center"
        justify="space-between"
        borderBottom="1px solid"
        borderColor="solid.50"
      >
        <Flex align="center" gap="10px">
          <Text textStyle="l-s" fontWeight="600" color="solid.700">
            Candidates by Vacancy
          </Text>
          <Box
            display="inline-flex"
            px="10px"
            py="3px"
            borderRadius="full"
            bg="solid.50"
            border="1px solid"
            borderColor="solid.400"
          >
            <Text textStyle="b-xs" fontWeight="500" color="solid.400">
              {vacancies.reduce((sum, v) => sum + v.candidate_count, 0)} candidates
            </Text>
          </Box>
        </Flex>
      </Flex>

      {/* Vacancy sections */}
      <Box>
        {vacancies.map((vacancy) => {
          const isExpanded = expandedVacancies.has(vacancy.id)
          const vacancyKey = vacancy.id ?? '__unassigned'

          return (
            <Box key={vacancyKey}>
              {/* Vacancy row */}
              <Flex
                px="20px"
                py="14px"
                align="center"
                justify="space-between"
                cursor="pointer"
                onClick={() => toggleExpand(vacancy.id)}
                borderBottom="1px solid"
                borderColor="solid.50"
                borderLeft="3px solid"
                borderLeftColor={isExpanded ? 'primary.100' : 'transparent'}
                _hover={{ bg: 'solid.25' }}
                transition="background 0.15s"
              >
                <Flex align="center" gap="10px" minW={0}>
                  <Box
                    w="8px"
                    h="8px"
                    borderRadius="full"
                    bg={vacancy.id ? 'primary.100' : 'solid.300'}
                    flexShrink={0}
                  />
                  <Text textStyle="l-s" fontWeight="600" color="solid.700" isTruncated>
                    {vacancy.title}
                  </Text>
                  {vacancy.country && (
                    <Flex align="center" gap="5px" flexShrink={0}>
                      <CircleFlag
                        countryCode={vacancy.country.toLowerCase()}
                        style={{ width: '16px', height: '16px' }}
                      />
                      <Text textStyle="b-xs" color="solid.500" fontWeight="500">
                        {vacancy.country}
                      </Text>
                    </Flex>
                  )}
                  {vacancy.job_profile && (
                    <>
                      <Box w="1px" h="14px" bg="solid.100" flexShrink={0} />
                      <Text textStyle="b-xs" color="solid.400" isTruncated>
                        {vacancy.job_profile.title}
                      </Text>
                    </>
                  )}
                </Flex>
                <Flex align="center" gap="10px" flexShrink={0}>
                  <Box
                    display="inline-flex"
                    px="10px"
                    py="3px"
                    borderRadius="full"
                    bg="solid.50"
                    border="1px solid"
                    borderColor="solid.400"
                  >
                    <Text textStyle="b-xs" fontWeight="500" color="solid.400">
                      {vacancy.candidate_count}
                    </Text>
                  </Box>
                  <Text
                    color="solid.300"
                    fontSize="10px"
                    transform={isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'}
                    transition="transform 0.2s"
                    userSelect="none"
                  >
                    &#9660;
                  </Text>
                </Flex>
              </Flex>

              {/* Expanded candidate list */}
              {isExpanded && (
                <Box bg="solid.25">
                  {vacancy.candidates.length > 0 ? (
                    <>
                      {/* Column header */}
                      <Flex
                        px="20px"
                        py="8px"
                        borderBottom="1px solid"
                        borderColor="solid.50"
                      >
                        <Text fontSize="12px" fontWeight="500" color="solid.400" w="28%">Candidate</Text>
                        <Text fontSize="12px" fontWeight="500" color="solid.400" w="14%">Status</Text>
                        <Text fontSize="12px" fontWeight="500" color="solid.400" w="14%">Stage</Text>
                        <Text fontSize="12px" fontWeight="500" color="solid.400" w="14%">Interviews</Text>
                        <Text fontSize="12px" fontWeight="500" color="solid.400" w="15%">Source</Text>
                        <Text fontSize="12px" fontWeight="500" color="solid.400" w="15%">Applied</Text>
                      </Flex>

                      {vacancy.candidates.map((c: VacancyOverviewCandidate) => {
                        const statusColors = CANDIDATE_STATUS_COLORS[c.status as CandidateStatus] || {
                          bg: 'solid.50',
                          text: 'solid.700',
                        }
                        const sourceColors = SOURCE_COLORS[c.source?.toLowerCase()] || { bg: 'solid.25', color: 'solid.400' }

                        return (
                          <Flex
                            key={c.id}
                            px="20px"
                            py="12px"
                            align="center"
                            borderBottom="1px solid"
                            borderColor="solid.50"
                            cursor="pointer"
                            bg="white"
                            _hover={{ bg: 'solid.25' }}
                            transition="background 0.15s"
                            onClick={() => navigate(`/user/recruitment/candidates/${c.id}`)}
                          >
                            {/* Candidate */}
                            <Flex w="28%" align="center" gap="10px" minW={0} pr="8px">
                              <Avatar
                                boxSize="28px"
                                name={`${c.first_name} ${c.last_name}`}
                                sx={{ '& > div': { fontSize: '10px' } }}
                              />
                              <Box minW={0}>
                                <Text textStyle="l-xs" fontWeight="500" isTruncated>
                                  {c.first_name} {c.last_name}
                                </Text>
                                {c.email && (
                                  <Text textStyle="b-xs" color="solid.300" fontSize="11px" isTruncated>
                                    {c.email}
                                  </Text>
                                )}
                              </Box>
                            </Flex>

                            {/* Status */}
                            <Box w="14%">
                              <Box
                                display="inline-flex"
                                px="10px"
                                py="3px"
                                borderRadius="full"
                                bg={statusColors.bg}
                                border="1px solid"
                                borderColor={statusColors.text}
                              >
                                <Text textStyle="b-xs" fontWeight="500" color={statusColors.text} textTransform="capitalize">
                                  {CANDIDATE_STATUS_LABELS[c.status as CandidateStatus] || c.status}
                                </Text>
                              </Box>
                            </Box>

                            {/* Stage dots */}
                            <Box w="14%">
                              <InterviewDots count={c.interview_stage || 0} />
                            </Box>

                            {/* Interview count */}
                            <Box w="14%">
                              <Text textStyle="l-xs" color="solid.700">
                                {c.interview_count}
                              </Text>
                            </Box>

                            {/* Source */}
                            <Box w="15%">
                              <Box
                                display="inline-flex"
                                px="10px"
                                py="3px"
                                borderRadius="full"
                                bg={sourceColors.bg}
                                border="1px solid"
                                borderColor={sourceColors.color}
                              >
                                <Text textStyle="b-xs" fontWeight="500" color={sourceColors.color} textTransform="capitalize">
                                  {c.source || '\u2014'}
                                </Text>
                              </Box>
                            </Box>

                            {/* Applied date */}
                            <Box w="15%">
                              <Text textStyle="b-xs" color="solid.400">
                                {c.applied_at ? new Date(c.applied_at).toLocaleDateString() : 'N/A'}
                              </Text>
                            </Box>
                          </Flex>
                        )
                      })}
                    </>
                  ) : (
                    <Flex px="20px" py="16px" justify="center">
                      <Text textStyle="b-xs" color="solid.300">
                        No candidates for this vacancy yet
                      </Text>
                    </Flex>
                  )}
                </Box>
              )}
            </Box>
          )
        })}
      </Box>
    </Card>
  )
}

export default VacancyOverview
